import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileText, Save, Printer, X, ChevronDown, ChevronUp, Check, Settings,
} from 'lucide-react';
import { authApi, clientsApi, documentsApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { SignaturePad } from '../SignaturePad';

// --- Interfaces ---

interface TemplateField {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'select' | 'checkbox' | 'date' | 'signature';
  required?: boolean;
  placeholder?: string;
  options?: string[];
  prefilled_from?: string;
}

interface TemplateSection {
  id: string;
  title: string;
  enabled: boolean;
  required?: boolean;
  order: number;
  text: string;
  fields: TemplateField[];
}

interface ContractTemplate {
  id: string;
  name: string;
  description: string;
  is_default: boolean;
  is_system: boolean;
  sections: TemplateSection[];
  created_at: string;
  updated_at: string;
}

interface Props {
  template: ContractTemplate;
  clientId?: string;
  onSaved?: (id: string) => void;
  onClose?: () => void;
}

export type { ContractTemplate, TemplateSection, TemplateField };

// --- Helpers ---

/** Parse sections: may come as JSON string or already as array */
function parseSections(raw: TemplateSection[] | string): TemplateSection[] {
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as TemplateSection[];
    } catch {
      return [];
    }
  }
  return Array.isArray(raw) ? raw : [];
}

/** Build initial values from template fields */
function buildInitialValues(sections: TemplateSection[]): Record<string, any> {
  const vals: Record<string, any> = {};
  for (const section of sections) {
    for (const field of section.fields) {
      switch (field.type) {
        case 'text':
        case 'textarea':
        case 'number':
        case 'date':
        case 'signature':
          vals[field.id] = '';
          break;
        case 'select':
          vals[field.id] = field.options?.[0] ?? '';
          break;
        case 'checkbox':
          vals[field.id] = false;
          break;
        default:
          vals[field.id] = '';
      }
    }
  }
  return vals;
}

/** Replace {field_id} placeholders in section text with actual values */
function interpolateText(text: string, values: Record<string, any>): string {
  return text.replace(/\{(\w+)\}/g, (match, fieldId) => {
    const val = values[fieldId];
    if (val === undefined || val === null || val === '') return '___';
    if (typeof val === 'boolean') return val ? 'Ja' : 'Nein';
    return String(val);
  });
}

/** HTML-escape for generated document */
function escHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// --- CSS Variable Reader ---

function getContractColors() {
  const s = getComputedStyle(document.documentElement);
  return {
    primary: s.getPropertyValue('--primary').trim() || '#8B6F47',
    primaryDark: s.getPropertyValue('--primary-dark').trim() || '#5C4330',
    bg: s.getPropertyValue('--bg').trim() || '#FAF8F5',
    border: s.getPropertyValue('--border').trim() || '#E8E0D4',
  };
}

// --- HTML Generator ---

function generateContractHtml(template: ContractTemplate, values: Record<string, any>): string {
  const colors = getContractColors();
  const sections = parseSections(template.sections).filter((s) => s.enabled).sort((a, b) => a.order - b.order);
  const today = new Date().toLocaleDateString('de-DE');
  const checkmark = (val: boolean) => (val ? '&#9745;' : '&#9744;');

  let sectionsHtml = '';

  for (const section of sections) {
    let sectionContent = '';

    // Section text with interpolation
    if (section.text) {
      const interpolated = interpolateText(section.text, values);
      sectionContent += `<div class="info-box">${escHtml(interpolated)}</div>\n`;
    }

    // Fields
    for (const field of section.fields) {
      const val = values[field.id];

      if (field.type === 'checkbox') {
        sectionContent += `<div class="check-item">${checkmark(!!val)} ${escHtml(field.label)}</div>\n`;
      } else if (field.type === 'signature') {
        if (val) {
          sectionContent += `<div class="sig-block"><p>${escHtml(field.label)}</p><img src="${val}" /></div>\n`;
        } else {
          sectionContent += `<div class="sig-block"><p>${escHtml(field.label)}</p><div class="sig-line"></div></div>\n`;
        }
      } else if (val !== undefined && val !== null && val !== '') {
        const displayVal = field.type === 'date' && val
          ? new Date(val).toLocaleDateString('de-DE')
          : String(val);
        sectionContent += `<div class="field"><span class="label">${escHtml(field.label)}:</span> <span class="value">${escHtml(displayVal)}</span></div>\n`;
      }
    }

    // Skip empty sections (no text, no filled fields)
    if (!sectionContent.trim()) continue;

    sectionsHtml += `<h2>${escHtml(section.title)}</h2>\n`;
    sectionsHtml += sectionContent;
  }

  return `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="UTF-8">
<title>${escHtml(template.name)}</title>
<style>
  @page { size: A4; margin: 20mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Georgia, 'Palatino Linotype', serif; font-size: 11pt; line-height: 1.6; color: #1a1a1a; background: #fff; padding: 40px; max-width: 800px; margin: 0 auto; }
  h1 { font-size: 18pt; color: ${colors.primary}; border-bottom: 3px solid ${colors.primaryDark}; padding-bottom: 8px; margin-bottom: 24px; }
  h2 { font-size: 12pt; color: ${colors.primary}; margin: 20px 0 8px; padding-bottom: 4px; border-bottom: 1px solid ${colors.border}; }
  .subtitle { font-size: 9pt; color: #888; margin-bottom: 24px; }
  .field { margin-bottom: 6px; }
  .label { font-weight: bold; color: ${colors.primary}; }
  .value { color: #333; }
  .info-box { background: ${colors.bg}; border-left: 3px solid ${colors.primaryDark}; padding: 10px 14px; margin: 10px 0; font-size: 10pt; line-height: 1.6; white-space: pre-wrap; }
  .check-item { margin: 4px 0; font-size: 10pt; }
  .sig-block { margin-top: 24px; }
  .sig-block p { font-weight: bold; font-size: 10pt; margin-bottom: 8px; color: ${colors.primary}; }
  .sig-block img { max-height: 70px; }
  .sig-line { border-bottom: 1px solid #999; height: 50px; }
  .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid ${colors.border}; font-size: 8pt; color: #999; text-align: center; }
  @media print {
    body { padding: 0; }
    .no-print { display: none; }
  }
</style>
</head>
<body>

<h1>${escHtml(template.name)}</h1>
<div class="subtitle">Erstellt am ${today}${template.description ? ' — ' + escHtml(template.description) : ''}</div>

${sectionsHtml}

<div class="footer">
  Erstellt mit CoachDesk365 &mdash; ${today}
</div>

<div class="no-print" style="text-align: center; margin-top: 24px;">
  <button onclick="window.print()" style="background:${colors.primaryDark};color:#fff;border:none;padding:10px 28px;border-radius:6px;font-size:14px;cursor:pointer;font-weight:600;">
    Drucken / Als PDF speichern
  </button>
</div>

</body>
</html>`;
}

// --- Component ---

export default function VertragsRenderer({ template, clientId, onSaved, onClose }: Props) {
  const { showToast } = useToast();

  // Parse sections once
  const sections = useMemo(
    () => parseSections(template.sections).filter((s) => s.enabled).sort((a, b) => a.order - b.order),
    [template.sections],
  );

  // State
  const [values, setValues] = useState<Record<string, any>>(() => buildInitialValues(sections));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedCoachSignature, setSavedCoachSignature] = useState('');
  const [signatureMode, setSignatureMode] = useState<Record<string, 'saved' | 'new'>>({});
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const s of sections) {
      init[s.id] = true;
    }
    return init;
  });

  // Prefill from coach profile + saved signature
  useEffect(() => {
    (async () => {
      try {
        const [profileRes, sigRes] = await Promise.all([
          authApi.me(),
          authApi.getSignature(),
        ]);
        const p = profileRes.data;
        const sig = sigRes.data?.signature || '';
        setSavedCoachSignature(sig);

        const prefillMap: Record<string, any> = {
          'settings.business_name': p.business_name || `${p.first_name} ${p.last_name}`,
          'settings.address': p.address_line1
            ? `${p.address_line1}, ${p.zip_code || ''} ${p.city || ''}`
            : '',
          'settings.email': p.email || '',
          'settings.phone': p.phone || '',
          'settings.certifications': p.certifications || '',
          'settings.association_membership': p.association_membership || '',
          'settings.hourly_rate': p.hourly_rate ? String(p.hourly_rate) : '',
          'settings.session_duration': p.session_duration ? String(p.session_duration) : '',
          'settings.cancellation_hours': p.cancellation_hours ? String(p.cancellation_hours) : '',
        };

        setValues((prev) => {
          const next = { ...prev };
          const modes: Record<string, 'saved' | 'new'> = {};
          for (const section of sections) {
            for (const field of section.fields) {
              if (field.prefilled_from && prefillMap[field.prefilled_from] !== undefined) {
                next[field.id] = prefillMap[field.prefilled_from];
              }
              // Auto-set coach signature from saved
              if (field.type === 'signature' && field.id.includes('coach') && sig) {
                next[field.id] = sig;
                modes[field.id] = 'saved';
              }
            }
          }
          setSignatureMode((prev) => ({ ...prev, ...modes }));
          return next;
        });
      } catch {
        /* ignore - profile may not load in all contexts */
      }
    })();
  }, [sections]);

  // Prefill from client data
  useEffect(() => {
    if (!clientId) return;
    (async () => {
      try {
        const res = await clientsApi.get(clientId);
        const c = res.data;

        const clientPrefillMap: Record<string, any> = {
          'client.name': `${c.first_name} ${c.last_name}`,
          'client.email': c.email || '',
          'client.phone': c.phone || '',
        };

        setValues((prev) => {
          const next = { ...prev };
          for (const section of sections) {
            for (const field of section.fields) {
              if (field.prefilled_from && clientPrefillMap[field.prefilled_from] !== undefined) {
                next[field.id] = clientPrefillMap[field.prefilled_from];
              }
            }
          }
          return next;
        });
      } catch {
        /* ignore */
      }
    })();
  }, [clientId, sections]);

  // --- Handlers ---

  const handleChange = useCallback((fieldId: string, value: any) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
    setSaved(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, type, value } = e.target;
      if (type === 'checkbox') {
        handleChange(name, (e.target as HTMLInputElement).checked);
      } else {
        handleChange(name, value);
      }
    },
    [handleChange],
  );

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  }, []);

  // --- Save ---

  const handleSave = async () => {
    setSaving(true);
    try {
      const html = generateContractHtml(template, values);
      const dateStr = new Date().toISOString().split('T')[0];
      const fileName = `${dateStr}_${template.name.replace(/\s+/g, '-')}.html`;

      if (clientId) {
        const res = await documentsApi.upload({
          client_id: clientId,
          html_content: html,
          title: template.name,
          document_type: 'contract',
          file_name: fileName,
        });
        setSaved(true);
        onSaved?.(res.data.id);
      } else {
        // Download as HTML
        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
        setSaved(true);
        onSaved?.(fileName);
      }
    } catch {
      showToast('Fehler beim Speichern des Vertrags.', 'error');
    }
    setSaving(false);
  };

  // --- Print ---

  const handlePrint = () => {
    const html = generateContractHtml(template, values);
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      setTimeout(() => win.print(), 300);
    }
  };

  // --- Section Header ---

  const SectionHeader = ({ section }: { section: TemplateSection }) => (
    <div
      className="flex-between"
      style={{ cursor: 'pointer', marginBottom: expandedSections[section.id] ? 12 : 0 }}
      onClick={() => toggleSection(section.id)}
    >
      <h4 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
        {section.title}
        {section.required && (
          <span style={{ color: 'var(--primary)', fontSize: '0.75rem', fontWeight: 400 }}>(Pflichtabschnitt)</span>
        )}
      </h4>
      {expandedSections[section.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
    </div>
  );

  // --- Prefilled indicator ---

  const PrefilledIndicator = () => (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: '0.72rem',
        color: 'var(--primary)',
        fontWeight: 400,
        marginLeft: 6,
      }}
    >
      <Settings size={10} />
      (aus Geschäftsdaten)
    </span>
  );

  // --- Field Renderer ---

  const renderField = (field: TemplateField) => {
    const val = values[field.id];
    const isPrefilled = !!field.prefilled_from;

    const wrapperStyle: React.CSSProperties = isPrefilled
      ? { borderLeft: '3px solid var(--primary)', paddingLeft: 10, marginBottom: 12 }
      : { marginBottom: 12 };

    switch (field.type) {
      case 'text':
        return (
          <div key={field.id} className="form-group" style={wrapperStyle}>
            <label>
              {field.label}
              {field.required && <span style={{ color: 'var(--primary)' }}> *</span>}
              {isPrefilled && <PrefilledIndicator />}
            </label>
            <input
              type="text"
              name={field.id}
              value={val || ''}
              onChange={handleInputChange}
              placeholder={field.placeholder || ''}
            />
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id} className="form-group" style={wrapperStyle}>
            <label>
              {field.label}
              {field.required && <span style={{ color: 'var(--primary)' }}> *</span>}
              {isPrefilled && <PrefilledIndicator />}
            </label>
            <textarea
              name={field.id}
              value={val || ''}
              onChange={handleInputChange}
              rows={3}
              placeholder={field.placeholder || ''}
            />
          </div>
        );

      case 'number':
        return (
          <div key={field.id} className="form-group" style={wrapperStyle}>
            <label>
              {field.label}
              {field.required && <span style={{ color: 'var(--primary)' }}> *</span>}
              {isPrefilled && <PrefilledIndicator />}
            </label>
            <input
              type="number"
              name={field.id}
              value={val || ''}
              onChange={handleInputChange}
              placeholder={field.placeholder || ''}
            />
          </div>
        );

      case 'select':
        return (
          <div key={field.id} className="form-group" style={wrapperStyle}>
            <label>
              {field.label}
              {field.required && <span style={{ color: 'var(--primary)' }}> *</span>}
              {isPrefilled && <PrefilledIndicator />}
            </label>
            <select name={field.id} value={val || ''} onChange={handleInputChange}>
              {(field.options || []).map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        );

      case 'checkbox':
        return (
          <label key={field.id} className="consent-checkbox-item" style={wrapperStyle}>
            <input
              type="checkbox"
              name={field.id}
              checked={!!val}
              onChange={handleInputChange}
            />
            <span>
              {field.label}
              {field.required && <span style={{ color: 'var(--primary)' }}> *</span>}
            </span>
          </label>
        );

      case 'date':
        return (
          <div key={field.id} className="form-group" style={{ ...wrapperStyle, maxWidth: 250 }}>
            <label>
              {field.label}
              {field.required && <span style={{ color: 'var(--primary)' }}> *</span>}
              {isPrefilled && <PrefilledIndicator />}
            </label>
            <input
              type="date"
              name={field.id}
              value={val || ''}
              onChange={handleInputChange}
            />
          </div>
        );

      case 'signature':
        return (
          <div key={field.id} className="signature-block" style={wrapperStyle}>
            <div className="signature-block-title">
              {field.label}
              {field.required && <span style={{ color: 'var(--primary)' }}> *</span>}
            </div>
            {renderSignatureField(field)}
          </div>
        );

      default:
        return null;
    }
  };

  // --- Signature field with saved-signature toggle for coach ---

  const renderSignatureField = (field: TemplateField) => {
    const isCoachSig = field.id.includes('coach');
    const mode = signatureMode[field.id] || 'new';
    const val = values[field.id];

    // If it's a coach signature field and we have a saved signature, show the toggle
    if (isCoachSig && savedCoachSignature) {
      return (
        <div>
          {/* Radio toggle */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                cursor: 'pointer',
                fontSize: '0.85rem',
                color: mode === 'saved' ? 'var(--primary)' : 'var(--text-light)',
                fontWeight: mode === 'saved' ? 600 : 400,
              }}
            >
              <input
                type="radio"
                name={`sig_mode_${field.id}`}
                checked={mode === 'saved'}
                onChange={() => {
                  setSignatureMode((prev) => ({ ...prev, [field.id]: 'saved' }));
                  handleChange(field.id, savedCoachSignature);
                }}
                style={{ accentColor: 'var(--primary)' }}
              />
              Gespeicherte Unterschrift
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                cursor: 'pointer',
                fontSize: '0.85rem',
                color: mode === 'new' ? 'var(--primary)' : 'var(--text-light)',
                fontWeight: mode === 'new' ? 600 : 400,
              }}
            >
              <input
                type="radio"
                name={`sig_mode_${field.id}`}
                checked={mode === 'new'}
                onChange={() => {
                  setSignatureMode((prev) => ({ ...prev, [field.id]: 'new' }));
                  handleChange(field.id, '');
                }}
                style={{ accentColor: 'var(--primary)' }}
              />
              Neue Unterschrift
            </label>
          </div>

          {mode === 'saved' ? (
            <div
              style={{
                background: 'var(--bg)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                padding: 12,
                textAlign: 'center',
              }}
            >
              <img
                src={savedCoachSignature}
                alt="Gespeicherte Coach-Unterschrift"
                style={{ maxHeight: 70, display: 'block', margin: '0 auto' }}
              />
            </div>
          ) : (
            <SignaturePad
              value={val || ''}
              onChange={(sig) => handleChange(field.id, sig)}
              height={80}
            />
          )}
        </div>
      );
    }

    // Default: plain signature pad
    return (
      <SignaturePad
        value={val || ''}
        onChange={(sig) => handleChange(field.id, sig)}
        height={80}
      />
    );
  };

  // --- Render ---

  return (
    <div className="card" style={{ maxWidth: 800, margin: '0 auto' }}>
      {/* Header */}
      <div className="flex-between mb-16">
        <div>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FileText size={22} style={{ color: 'var(--primary)' }} />
            {template.name}
          </h2>
          {template.description && (
            <p className="text-muted text-sm" style={{ marginTop: 4 }}>
              {template.description}
            </p>
          )}
        </div>
        <div className="flex gap-8">
          <button className="btn btn-sm btn-secondary" onClick={handlePrint} title="Drucken / PDF">
            <Printer size={14} /> Drucken
          </button>
          <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={14} /> {saving ? 'Wird gespeichert...' : 'Speichern'}
          </button>
          {onClose && (
            <button className="btn btn-sm btn-secondary" onClick={onClose} title="Schließen">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Success banner */}
      {saved && (
        <div
          style={{
            background: '#E8F5E8',
            color: 'var(--success)',
            padding: '10px 16px',
            borderRadius: 'var(--radius-sm)',
            marginBottom: 16,
            fontSize: '0.88rem',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Check size={16} /> Vertrag wurde erfolgreich gespeichert.
        </div>
      )}

      {/* Sections */}
      {sections.map((section, idx) => {
        const isExpanded = expandedSections[section.id];

        // Separate signature fields from regular fields
        const regularFields = section.fields.filter((f) => f.type !== 'signature');
        const signatureFields = section.fields.filter((f) => f.type === 'signature');

        return (
          <div key={section.id} className="block-form-section">
            <SectionHeader section={section} />

            {isExpanded && (
              <>
                {/* Section info text */}
                {section.text && (
                  <div
                    style={{
                      background: 'var(--bg)',
                      borderRadius: 'var(--radius-sm)',
                      padding: 14,
                      marginBottom: 12,
                      fontSize: '0.85rem',
                      lineHeight: 1.7,
                      color: 'var(--text-light)',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    <p style={{ margin: 0 }}>{interpolateText(section.text, values)}</p>
                  </div>
                )}

                {/* Regular fields */}
                {regularFields.map((field) => renderField(field))}

                {/* Signature fields in a grid */}
                {signatureFields.length > 0 && (
                  <div className="signatures-grid">
                    {signatureFields.map((field) => renderField(field))}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

      {/* Bottom actions */}
      <div className="flex gap-8" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
        <button className="btn btn-secondary" onClick={handlePrint}>
          <Printer size={16} /> Drucken / PDF
        </button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={16} /> {saving ? 'Speichern...' : 'Vertrag speichern'}
        </button>
      </div>
    </div>
  );
}
